import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { stockBatchesApi } from '../stockApi';
import type { StockBatchListItem } from '../stockTypes';

/**
 * Write off quantity that physically existed.
 *
 * **Its own modal behind its own key**, rather than a field on the edit form. Destroying stock
 * and correcting a note are not the same privilege, and this is the one batch operation that
 * cannot be undone by editing the row back — a disposal appends a ledger line asserting that
 * this organization destroyed something, where an adjustment records a figure being corrected.
 *
 * The quantity is asked for POSITIVE — how much to destroy — and the ledger line written from
 * it is negative. Two spellings of one number in a single form is how a reader eventually
 * enters the wrong one.
 */
export function DisposeStockModal({
  open,
  batch,
  onClose,
  onSaved,
}: {
  open: boolean;
  batch: StockBatchListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const quantityNumber = Number(quantity);
  const withinStock = Number.isFinite(quantityNumber) && quantityNumber <= batch.quantityOnHand;

  const valid =
    quantity.trim() !== '' &&
    Number.isFinite(quantityNumber) &&
    quantityNumber > 0 &&
    withinStock &&
    reason.trim().length > 0;

  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await stockBatchesApi.dispose(batch.id, {
        quantity: quantityNumber,
        reason: reason.trim(),
      });

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not record the disposal.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'dispose-stock' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Dispose of stock"
      size="md"
      id="dispose-stock-modal"
      /*
        NO Autogenerate. The contract is that the primary action enables on the first try so the
        screen behind a form gets exercised — and a generated disposal writes an irreversible
        ledger line asserting stock was destroyed. A button that fabricates that is a button
        that eventually fabricates it on a real lot.
      */
      confirm={{
        id: 'dispose-stock-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="dispose-stock-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="dispose-stock-submit"
            variant="danger"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Recording"
          >
            Dispose
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Batch', value: batch.batchNumber, mono: true },
            { label: 'Item', value: `${batch.sku} — ${batch.itemName}` },
            { label: 'Facility', value: batch.storageFacilityName },
            { label: 'On hand', value: `${batch.quantityOnHand} ${batch.unitCode}`, mono: true },
          ]}
        />

        {/*
          The consequence goes on the FORM, before the tick — never appended to the thing the
          reader is already clicking past.
        */}
        <p className="text-sm text-slate-600">
          This records stock as destroyed and appends a line to the ledger saying so. It cannot be
          undone by editing the batch back; putting the quantity back would be a separate
          correction, and the disposal would stay on the record.
        </p>

        <TextField
          id="dispose-stock-quantity"
          label="Quantity to dispose of"
          required
          type="number"
          min={1}
          max={batch.quantityOnHand}
          description="How much is being destroyed. The ledger line written from it is negative."
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          disabled={saving}
          error={
            withinStock
              ? error?.fieldError('quantity')
              : `This lot holds ${batch.quantityOnHand}.`
          }
        />

        <TextField
          id="dispose-stock-reason"
          label="Why?"
          required
          description="Recorded on the ledger line. Expired, damaged in store, condemned after inspection."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('reason')}
        />
      </div>
    </Modal>
  );
}
